import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { F, Btn } from "@/lib/bricks";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { sourceLabel } from "@/lib/applicantSource";
import { fmtDay } from "@/lib/dates";
import { Page, PageHeader, Stats, StatusTabs, Toolbar, Search, SmallSelect, Controls, List, Row, Meta, Empty, Loading, TextLink, usePalette, humanize } from "@/components/calm/Calm";
import { ReviewScorePill } from "@/components/hiring/ApplicantReview";
import { TierInsuranceFlag } from "@/components/hiring/InsuranceSection";
import { TradeBadge, VettingChip, tradeLabel } from "@/components/hiring/VettingSection";

const ONBOARDING_PACK_URL = "https://github.com/bricks-group/bricks-internal/tree/main/hiring/onboarding";

const TIER_LABEL = { 1: 'Tier C', 2: 'Tier B', 3: 'Tier A' };
const STATUS_LABEL = { pending: 'Needs review', approved: 'Approved', rejected: 'Rejected', terminated: 'Terminated' };

const TABS = [
  { key: 'Pending', label: 'Needs review' },
  { key: 'Onboarding', label: 'Onboarding' },
  { key: 'Approved', label: 'Approved' },
  { key: 'Rejected', label: 'Rejected' },
  { key: 'Terminated', label: 'Terminated' },
  { key: 'All', label: 'All' },
];
const EMPTY_COPY = {
  Pending: 'No applications waiting. New ones from the hiring page land here.',
  Onboarding: 'Nobody is mid-setup. Approved vendors show here until every onboarding step is done.',
  Approved: 'No approved vendors yet.',
  Rejected: 'No rejected applications.',
  Terminated: 'No terminated vendors.',
  All: 'No applications yet.',
};

export default function ApplicantsPage() {
  const { p, dark } = usePalette();
  const { user } = useAuth();
  const showOnboardingPack = !!user?.is_platform_owner;

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Pending');
  const [search, setSearch] = useState('');
  const [trade, setTrade] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get('/admin/vendors', { params: { status: 'all' } })
      .then(r => { setVendors(r.data || []); setLoading(false); })
      .catch((err) => {
        setLoading(false);
        const status = err?.response?.status;
        toast.error(status === 401 || status === 403
          ? 'Sign in as platform owner to see applicants'
          : 'Could not load applicants');
      });
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action, tier) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/vendors/${id}`, { action, tier });
      toast.success({ approve: 'Applicant approved', reject: 'Applicant rejected', unreject: 'Moved back to pending' }[action] || 'Tier updated');
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || (typeof d === 'string' ? d : null))) || 'Failed to update applicant');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = vendors.filter(v => {
    const matchesSearch = !search ||
      (v.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (v.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (v.vendor_type || '').toLowerCase().includes(search.toLowerCase());
    const matchesTab = activeTab === 'All'
      || (activeTab === 'Onboarding' ? v.onboarding?.state === 'onboarding' : v.onboarding_status === activeTab.toLowerCase());
    const matchesTrade = trade === 'all' || (v.vendor_type || 'photographer') === trade;
    return matchesSearch && matchesTab && matchesTrade;
  });

  const tradeOptions = [{ key: 'all', label: 'All trades' }].concat(
    [...new Map(vendors.map(v => [v.vendor_type || 'photographer', tradeLabel(v)])).entries()]
      .sort((a, b) => a[1].localeCompare(b[1])).map(([key, label]) => ({ key, label })));
  const pendingCount = vendors.filter(v => v.onboarding_status === 'pending').length;
  const approvedCount = vendors.filter(v => v.onboarding_status === 'approved').length;
  const onboardingCount = vendors.filter(v => v.onboarding?.state === 'onboarding').length;
  // Approved vendors: live, paused, or n of 5 onboarding steps done.
  const statusFor = (v) => {
    if (v.terminated_at || v.onboarding_status === 'terminated') return 'Terminated';
    const o = v.onboarding;
    if (v.onboarding_status === 'approved' && o) {
      if (o.state === 'live') return 'Live';
      if (o.state === 'paused') return 'Paused';
      return `Onboarding ${o.done}/${o.total}`;
    }
    return STATUS_LABEL[v.onboarding_status] || humanize(v.onboarding_status) || 'Unknown';
  };

  if (loading) return <Loading />;

  const small = { padding: '6px 12px', fontSize: 12 };

  return (
    <Page testid="applicants-page">
      <PageHeader
        title="Hiring"
        subtitle="Photographer, stager and cleaner applications"
        action={showOnboardingPack ? (
          <TextLink href={ONBOARDING_PACK_URL} testid="hiring-onboarding-pack">Onboarding pack</TextLink>
        ) : null}
      />

      <Stats items={[
        { label: 'Needs review', value: pendingCount, attention: pendingCount > 0 },
        { label: 'Onboarding', value: onboardingCount },
        { label: 'Approved', value: approvedCount },
        { label: 'All applicants', value: vendors.length },
      ]} />

      <Controls>
        <StatusTabs options={TABS} value={activeTab} onChange={setActiveTab} testid="applicants-status-filter" />
        <Toolbar>
          <Search value={search} onChange={setSearch} placeholder="Search name or email" />
          {tradeOptions.length > 2 && <SmallSelect value={trade} onChange={setTrade} options={tradeOptions} label="Trade" testid="applicants-trade-filter" />}
        </Toolbar>
      </Controls>

      {filtered.length === 0 ? (
        <Empty>{search ? `No applicants match "${search}".` : EMPTY_COPY[activeTab]}</Empty>
      ) : (
        <List testid="applicants-list">
          {filtered.map(v => {
            const isPending = v.onboarding_status === 'pending';
            const isBusy = busyId === v.id;
            const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
            const record = [
              { text: `Applied ${fmtDay(v.created_at)}` },
              !isPending ? { text: `${v.total_assignments || 0} jobs`, figure: true } : null,
              !isPending && v.on_time_rate ? { text: `${Math.round(v.on_time_rate * 100)}% on time`, figure: true } : null,
              sourceLabel(v.source) ? { text: `via ${sourceLabel(v.source)}` } : null,
              v.assignable === false && v.onboarding?.state !== 'onboarding' ? { text: 'Not assignable', attention: true } : null,
            ].filter(Boolean);
            return (
              <Row
                key={v.id}
                testid="applicant-row"
                onClick={() => navigate(`/hiring/${v.id}`)}
                title={v.name}
                meta={[v.title || v.role, v.email].filter(Boolean)}
                status={statusFor(v)}
                attention={isPending}
                next={
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px', minWidth: 0 }}>
                    <Meta parts={record} style={{ flex: '0 1 auto' }} />
                    <TradeBadge v={v} />
                    <VettingChip v={v} />
                    <ReviewScorePill review={v.applicant_review} dark={dark} />
                    <TierInsuranceFlag v={v} />
                    {isPending ? (
                      <span style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                        <Btn variant="ghost" disabled={isBusy} onClick={stop(() => act(v.id, 'reject'))} style={small}>Reject</Btn>
                        <Btn disabled={isBusy} onClick={stop(() => act(v.id, 'approve', 1))} style={small}>{isBusy ? 'Saving' : 'Approve, tier C'}</Btn>
                      </span>
                    ) : v.onboarding_status === 'approved' ? (
                      <select
                        aria-label={`Tier for ${v.name}`}
                        value={v.tier || 1}
                        disabled={isBusy}
                        onClick={e => e.stopPropagation()}
                        onChange={e => { e.stopPropagation(); act(v.id, 'set_tier', Number(e.target.value)); }}
                        style={{ border: 'none', background: 'transparent', color: p.ink2, fontFamily: F.display, fontSize: 12.5, cursor: 'pointer', outline: 'none', padding: 0 }}
                      >
                        {[1, 2, 3].map(t => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}
                      </select>
                    ) : v.onboarding_status === 'rejected' ? (
                      <span style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                        <Btn variant="ghost" disabled={isBusy} onClick={stop(() => act(v.id, 'unreject'))} style={small}>{isBusy ? 'Saving' : 'Undo reject'}</Btn>
                      </span>
                    ) : null}
                  </div>
                }
              />
            );
          })}
        </List>
      )}
    </Page>
  );
}
